/**
 * SRES · Simplified Flow — canonical 9-step linear wizard (Feb 2026 · v5).
 *
 * Client mandate (2026-02-29):
 *   Replace the current menu with this EXACT 9-step flow:
 *     (1) New Project        (2) Project Details    (3) File Upload
 *     (4) Validate & Auto-Fix (5) Logs & Maps        (6) Estimation Settings
 *     (7) Run Estimation      (8) Generate Report    (9) Download
 *   Remove complicated UI and duplicate menus. Every workflow move is
 *   through the in-page Back / Save & Continue buttons + the top step
 *   progress bar. NO changes to the backend — every step reuses an
 *   existing endpoint.
 *
 * Underlying 20-item WIZARD_STEPS registry is preserved for direct
 * URL access, but the primary UX presents exactly nine steps.
 */
import {
  Sparkle, IdentificationCard, UploadSimple, ShieldCheck,
  MapTrifold, Gear, Play, FileText, DownloadSimple,
  House, Buildings, Archive, ShieldCheck as ShieldCheckAdmin,
} from "@phosphor-icons/react";

// ═══════════════════════════════════════════════════════════════════
// SIDEBAR — exactly 4 items · unchanged from Feb 2026 · v4
// ═══════════════════════════════════════════════════════════════════
export const SIDEBAR_ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: House,
    to: "/app/dashboard", testid: "nav-dashboard",
    activePathTest: (p) => p === "/app" || p === "/app/dashboard"
      || p === "/app/portfolio" },
  { key: "projects", label: "Projects", icon: Buildings,
    to: "/app/projects", testid: "nav-projects",
    activePathTest: (p) => p === "/app/projects" || p === "/app/projects/new"
      || (p.startsWith("/app/projects/")
          && !p.match(/\/(reports|data-bank)(\/|$)/)
          && !p.startsWith("/app/superadmin")) },
  { key: "reports", label: "Reports & Downloads", icon: Archive,
    to: "/app/reports", testid: "nav-reports",
    activePathTest: (p) => p === "/app/reports"
      || /\/(reports|data-bank)(\/|$)/.test(p) },
  { key: "administration", label: "Administration", icon: ShieldCheckAdmin,
    to: "/app/superadmin", testid: "nav-admin",
    activePathTest: (p) => p.startsWith("/app/superadmin"),
    requiresRole: "superadmin" },
];

export function resolveActiveSidebar(path) {
  for (const item of SIDEBAR_ITEMS) {
    try { if (item.activePathTest(path)) return item.key; } catch { /* skip */ }
  }
  return null;
}

// ═══════════════════════════════════════════════════════════════════
// 9-STEP LINEAR WIZARD
//
// Order MUST NOT change — FlowNav derives neighbours from array index.
// Every reachable page in the primary user journey maps to exactly one
// of these nine steps. Additional/legacy screens remain URL-reachable
// (documented in `WIZARD_STEPS`) but are NOT surfaced in navigation.
// ═══════════════════════════════════════════════════════════════════
export const WORKFLOW_STAGES = [
  { key: "new_project", n: 1, label: "New Project",
    subtitle: "Create a new project workspace",
    estMin: 1,
    icon: Sparkle,
    url: () => `/app/projects/new`,
    wizardStepIds: [],
    pathMatches: (p) => p === "/app/projects/new" },

  { key: "project_details", n: 2, label: "Project Details",
    subtitle: "Identity · client · consultant · commodity · CRS",
    estMin: 2,
    icon: IdentificationCard,
    url: (pid) => `/app/projects/${pid}/setup`,
    wizardStepIds: ["project_setup"],
    pathMatches: (p) => /\/setup(\/|$|\?)/.test(p)
      && !p.includes("/setup/configuration") },

  { key: "file_upload", n: 3, label: "File Upload",
    subtitle: "Collar · survey · assay · lithology · boundary KML",
    estMin: 3,
    icon: UploadSimple,
    url: (pid) => `/app/projects/${pid}/wizard/data_upload`,
    wizardStepIds: ["data_upload", "boundary_crs"],
    pathMatches: (p) => p.includes("/wizard/data_upload")
      || p.includes("/wizard/boundary_crs") },

  { key: "validate_autofix", n: 4, label: "Validate & Auto-Fix",
    subtitle: "Cross-file checks · AI auto-corrections · manual review",
    estMin: 2,
    icon: ShieldCheck,
    url: (pid) => `/app/projects/${pid}/wizard/data_validation`,
    wizardStepIds: ["data_validation", "ai_review"],
    pathMatches: (p) => p.includes("/wizard/data_validation")
      || p.includes("/wizard/ai_review") },

  { key: "logs_maps", n: 5, label: "Logs & Maps",
    subtitle: "Continuous drillhole logs · GIS boundary & map gallery",
    estMin: 2,
    icon: MapTrifold,
    url: (pid) => `/app/projects/${pid}/logs-maps`,
    wizardStepIds: ["gis_maps"],
    pathMatches: (p) => p.includes("/logs-maps")
      || p.includes("/logs")
      || p.includes("/gis")
      || p.includes("/wizard/gis_maps")
      || p.includes("/wizard/borehole_logs") },

  { key: "estimation_settings", n: 6, label: "Estimation Settings",
    subtitle: "Density · cut-off · statistics · composites · domains · variogram",
    estMin: 5,
    icon: Gear,
    url: (pid) => `/app/projects/${pid}/wizard/estimation_params`,
    wizardStepIds: [
      "density_cutoff", "statistics", "composite", "domain",
      "variogram", "estimation_params",
    ],
    pathMatches: (p) => p.includes("/wizard/density_cutoff")
      || p.includes("/wizard/statistics")
      || p.includes("/wizard/composite")
      || p.includes("/wizard/domain")
      || p.includes("/wizard/variogram")
      || p.includes("/wizard/estimation_params") },

  { key: "run_estimation", n: 7, label: "Run Estimation",
    subtitle: "Block model · resource estimation · classification · validation",
    estMin: 3,
    icon: Play,
    url: (pid) => `/app/projects/${pid}/wizard/block_model`,
    wizardStepIds: [
      "block_model", "resource_estimation", "classification",
      "estimation_validation",
    ],
    pathMatches: (p) => p.includes("/wizard/block_model")
      || p.includes("/wizard/resource_estimation")
      || p.includes("/wizard/classification")
      || p.includes("/wizard/estimation_validation") },

  { key: "generate_report", n: 8, label: "Generate Report",
    subtitle: "Report type · AI narratives · CP/QP sign-off · publish",
    estMin: 2,
    icon: FileText,
    url: (pid) => `/app/projects/${pid}/wizard/cpr_config`,
    wizardStepIds: [
      "cpr_config", "ai_narratives", "final_review", "report_generation",
    ],
    pathMatches: (p) => p.includes("/wizard/cpr_config")
      || p.includes("/wizard/ai_narratives")
      || p.includes("/wizard/final_review")
      || p.includes("/wizard/report_generation")
      || p.includes("/wizard/cpr")
      || p.endsWith("/cpr") },

  { key: "download", n: 9, label: "Download",
    subtitle: "Preview · download · version history · exports",
    estMin: 1,
    icon: DownloadSimple,
    url: (pid) => `/app/projects/${pid}/reports`,
    wizardStepIds: ["data_bank"],
    pathMatches: (p) => /\/(reports|data-bank)(\/|$)/.test(p) },
];

/**
 * Total estimated end-to-end runtime for the 9-step flow.
 * Used by the wizard sidebar to render a "~N min end-to-end" hint.
 */
export const TOTAL_EST_MIN = WORKFLOW_STAGES
  .reduce((acc, s) => acc + (s.estMin || 0), 0);

/** Human-friendly duration label ("~2 min", "~5 min"). */
export function formatEstMin(min) {
  if (!min || min <= 0) return "";
  if (min < 1) return "< 1 min";
  return `~${min} min`;
}

/** Back-compat export. */
export const FLOW_SEQUENCE = WORKFLOW_STAGES;

/** Resolve the current workflow stage key from a URL path. */
export function resolveFlowKey(path) {
  for (const s of WORKFLOW_STAGES) {
    try { if (s.pathMatches(path)) return s.key; } catch { /* skip */ }
  }
  return null;
}

/** Resolve stage from a wizard step id (e.g. "domain" → "estimation_settings"). */
export function resolveStageForWizardStep(stepId) {
  if (!stepId) return null;
  for (const s of WORKFLOW_STAGES) {
    if ((s.wizardStepIds || []).includes(stepId)) return s;
  }
  return null;
}

/** Milestone before / after the given key, if any. */
export function flowNeighbours(currentKey) {
  const i = WORKFLOW_STAGES.findIndex((f) => f.key === currentKey);
  if (i === -1) return { prev: null, next: null, idx: -1 };
  return {
    idx: i,
    prev: i > 0 ? WORKFLOW_STAGES[i - 1] : null,
    next: i < WORKFLOW_STAGES.length - 1 ? WORKFLOW_STAGES[i + 1] : null,
  };
}

/**
 * GIS Studio internal sequence — Boundary → Map Gallery → Validation → Exports.
 */
export const GIS_INTERNAL_TABS = [
  { key: "boundary", label: "Boundary" },
  { key: "viewer",   label: "Map Gallery" },
  { key: "validate", label: "Validation" },
  { key: "exports",  label: "Exports" },
];

// ─── Deprecated exports (kept for back-compat of stale imports) ─────
export const GLOBAL_NAV = SIDEBAR_ITEMS.filter((i) => !i.requiresRole).slice(0, 2);
export const projectNav = () => [];
export const SETTINGS_NAV = SIDEBAR_ITEMS.find((i) => i.key === "administration") || null;
